import type { Dict } from "@/types/dict";
import {
  shallowRef,
  unref,
  type Ref,
  ref,
  type ShallowRef,
  type ComputedRef,
  computed,
  watch,
  isRef,
  customRef,
} from "vue";
import type {
  FormDataComputation,
  FormDataComputationDefinition,
  UseFormProps,
} from "./types";
import {
  getLayoutByFieldPath,
  getNextLayoutNode,
  patchArray,
  patchLayout,
  type FormLayoutDefinition,
  type FormLayoutNode,
} from "./layout";
import {
  schemaToState,
  type FormSchema,
  type ProcessedFormSchema,
} from "./schema";
import { getNextFormState, type FormUpdate } from "./state";
import {
  defineField,
  EFormFieldDefinitionAction,
  getFormFieldErrors,
  isFormField,
  isFormFieldDefinition,
  isFormFieldGroup,
  isFormFieldGroupDefinition,
  type FormField,
  type FormFieldDefinition,
  type FormFieldErrorWithPath,
  type FormFieldGroup,
  type FormFieldGroupDefinition,
  type FormFieldReference,
  type FormFieldReferenceAttributes,
} from "./field";
import { traverse } from "@/utils/object/traverse";
import { diffAndPatch, getObjectDiffs } from "@/utils/object/patch";
import { pick, type Get } from "@/utils/object/pick";
import { castToInput, castToState, castTypeNameToInput } from "./cast";
import { insert } from "@/utils/object/insert";
import { EFormEvent, type FormEventMap, type FormEventProducer } from "./event";
import { EventProducer } from "@/utils/event";
import { EFormEventHookOrder } from "./eventHook";
import { FormScope } from "./scope";
import { getRefId } from "findef";
import { isNullish } from "@/utils/is";

const clone = <T = any>(obj: T): T => JSON.parse(JSON.stringify(obj));

export class FormInstance<
  State extends Dict = Dict,
  ComputedData extends Dict = Dict,
  ExternalData extends Dict = Dict,
> {
  readonly __v_skip = true as const;
  props: UseFormProps<State, ComputedData, ExternalData>;
  state: Ref<State>;
  schema: Ref<ProcessedFormSchema<State, ComputedData>>;
  fields: Map<string, FormFieldReference<State, any, ComputedData>> = new Map();
  fieldList: Ref<FormFieldReference<State, any, ComputedData>[]> = ref([]);
  attributeRefs: Map<
    string,
    Ref<FormFieldReferenceAttributes<State, ComputedData>>
  > = new Map();
  layoutDefinition: ShallowRef<
    FormLayoutDefinition<State, ComputedData, ExternalData>
  >;
  layout: Ref<FormLayoutNode<State, ComputedData, ExternalData>>;
  layoutCache: Map<string, FormLayoutNode<State, ComputedData, ExternalData>> =
    new Map();
  lastUpdate: FormUpdate<State, ComputedData, ExternalData>;
  version: Ref<number> = ref(0);
  errors: Ref<FormFieldErrorWithPath[]> = ref([]);
  currentErrors: ComputedRef<FormFieldErrorWithPath[]> = computed(() =>
    this.errors.value.filter(
      (it) => !!getLayoutByFieldPath(it.path, this.layout.value),
    ),
  );
  touched: Ref<boolean> = ref(false);
  computedData: Ref<ComputedData> = ref<ComputedData>(
    {} as ComputedData,
  ) as Ref<ComputedData>;
  computations: Array<FormDataComputation<State, ComputedData>> = [];
  watchingState: Ref<State | null | undefined> = ref(null);
  originalState: State | null = null;
  forcedChanges: Ref<number> = ref<number>(0)

  hasChanges = computed(() => {
    if (this.forcedChanges.value > 0) return true;
    const initial = this.watchingState.value;
    const next = this.state.value;
    if (!initial) return false;
    const diffs = getObjectDiffs(initial, next);
    return diffs.length > 0;
  });

  constructor(props: UseFormProps<State, ComputedData, ExternalData>) {
    FormScope.currentForm = this;

    this.props = props;
    this.state = ref(schemaToState(props.schema)) as Ref<State>;
    this.layoutDefinition = shallowRef<
      FormLayoutDefinition<State, ComputedData, ExternalData>
    >(unref(props.layout));
    this.layout = ref(
      getNextLayoutNode(
        unref(props.layout),
        this.state.value,
        this.layoutCache,
        this,
      ),
    ) as unknown as any as Ref<
      FormLayoutNode<State, ComputedData, ExternalData>
    >;

    this.lastUpdate = getNextFormState<State, ComputedData, ExternalData>(
      {
        schema: props.schema,
        layout: this.layoutDefinition.value,
        cache: this.layoutCache,
        computedData: this.computedData.value,
      },
      this,
    );
    this.schema = ref<ProcessedFormSchema<State, ComputedData>>(
      this.lastUpdate.schema,
    ) as Ref<ProcessedFormSchema<State, ComputedData>>;
    this.createFields(this.schema.value);
    this.linkFields();
    this.initComputations();

    queueMicrotask(() => {
      this.linkLayoutsWithFields(this.layout.value);
      this.linkComputedWithFields();
    });

    if (isRef(props.layout)) {
      watch(
        props.layout,
        (nextLayout) => {
          this.setLayout(nextLayout);
          this.updateErrors();
        },
        { immediate: true },
      );
    }
  }

  reloadComputations() {
    this.computations.forEach((comp) => {
      comp.reload();
    });
    this.nextComputedData();
  }

  nextComputedData() {
    let patched = { ...this.computedData.value };
    this.computations.forEach((comp) => {
      patched = { ...patched, ...comp.update() };
    });
    this.computedData.value = patched;
  }

  initComputations() {
    (this.props.computed || []).forEach((comp) => {
      const computation = this.setupComputedData(comp);
      this.computations.push(computation);
    });
  }

  setupComputedData(
    comp: FormDataComputationDefinition<State, ComputedData>,
  ): FormDataComputation<State, ComputedData> {
    const initialNext = comp.next(this.state.value);
    const initialData = initialNext(this.state);
    const computation: FormDataComputation<State, ComputedData> = {
      activeDeps: [],
      next: initialNext,
      reload: () => {},
      update: () => initialData,
      needs: comp.needs || [],
      data: ref<ComputedData>(initialData) as Ref<ComputedData>,
    };

    computation.update = () => {
      const data = computation.next(this.state);
      computation.data.value = data;
      return data;
    };

    computation.reload = () => {
      computation.next = comp.next(this.state.value);
      computation.update();
    };
    return computation;
  }

  private linkComputedWithFields() {
    this.computations.forEach((comp) => {
      const needs = comp.needs;
      if (!needs) return;

      (comp.needs || []).forEach((path) => {
        if (!comp.activeDeps.includes(path)) {
          comp.activeDeps.push(path);
          const dep = this.getField(path);
          dep.emitter.subscribe(EFormEvent.FIELD_VALUE_CHANGE, () => {
            comp.reload();
          });
        }
      });
    });
  }

  private createFields(
    schema: FormSchema<State, State, true, ComputedData>,
    _crumbs: string[] = [],
  ): FormFieldReference<State, any, ComputedData>[] {
    const traverse = (
      schema: FormSchema<State, State, true, ComputedData>,
      crumbs: string[] = [],
    ) => {
      return Object.entries(schema)
        .map(([key, item]) => {
          if (isFormField<State>(item) || isFormFieldDefinition<State>(item)) {
            const path = isFormField(item)
              ? item.path
              : [...crumbs, key].join(".");
            const field = this.createField(path, item as unknown as any);
            return field;
          }
          if (isFormFieldGroupDefinition(item) || isFormFieldGroup(item)) {
            const path = [...crumbs, key].join(".");
            const field = this.createField(
              path,
              item as unknown as any,
              traverse(item.fields as unknown as any, [...crumbs, key]) as unknown as any,
            );
            return field;
          }
        })
        .filter((it) => typeof it !== "undefined" && it !== null)
        .flat()
        .filter((it) => typeof it !== "undefined" && it !== null);
    };

    const fields = traverse(schema);
    fields.forEach((field) => {
      this.fields.set(field.path, field);
    });
    this.fieldList.value = fields as unknown as any;
    return fields;
  }

  private linkLayoutsWithFields(
    layout: FormLayoutNode<State, ComputedData, ExternalData>,
  ) {
    const link = (
      layout: FormLayoutNode<State, ComputedData, ExternalData>,
    ) => {
      (layout.needs || []).forEach((path) => {
        const dep = this.getField(path);
        if (!layout.deps.includes(path)) {
          dep.emitter.subscribe(EFormEvent.FIELD_VALUE_CHANGE, (_ev) => {
            if (layout.componentConstructor) {
              layout.component.value = layout.componentConstructor(
                this.state.value,
                this,
              );
            }
            layout.version.value = layout.version.value + 1;
          });
          layout.deps.push(path);
        }
      });
      layout.children.value.forEach((child) => {
        link(child);
      });
    };
    link(layout);
  }

  private advanceField(field: FormFieldReference<State, any, ComputedData>) {
    const attribs = unref(field.attributes);
    const nextPatch = attribs.next
      ? attribs.next(this.state.value, this.computedData.value, {
          ...(attribs || {}),
          value: field.value.value,
        } as unknown as any)
      : {};

    if (field.isGroup) {
      const schem = field.schema;
      if (isFormFieldGroup(schem)) {
        insert(this.schema.value, [schem.schemaPath, "attributes"].join("."), {
          ...attribs,
          ...nextPatch,
        });
      }
      //field.attributes.value = {...field.attributes.value, ...nextPatch};
      ///this.setFieldAttributes(field.path, (attr) => ({...attr, ...nextPatch}))
    } else {
      if ("value" in nextPatch) {
        //this.setFieldValue(field.path, nextPatch.value)
        field.value.value = nextPatch.value;
      }
      if ("placeholder" in nextPatch && field.attributes.value) {
        field.attributes.value.placeholder = nextPatch.placeholder;
      }
      this.setFieldAttributes(field.path, (attr) => ({
        ...attr,
        ...nextPatch,
      }));
    }
  }

  private linkFields() {
    this.fields.forEach((field) => {
      const attribs = unref(field.attributes);
      if (attribs.dependsOn && Array.isArray(attribs.dependsOn)) {
        watch(attribs.dependsOn, () => {
          this.advanceField(field);
        }, { immediate: true });
      }

      Object.entries(field.attributes.value.hooks || {}).forEach(
        ([path, hook]) => {
          const dep = this.getField(path);
          dep.emitter.subscribe(EFormEvent.FIELD_VALUE_CHANGE, (_ev) => {
            if (hook.order === EFormEventHookOrder.EARLY) {
              const next = hook.fun(this.state.value);
              if ("value" in next) {
                this.setFieldValue(field.path, next.value);
              }
            } else {
              queueMicrotask(() => {
                const next = hook.fun(this.state.value);
                if ("value" in next) {
                  this.setFieldValue(field.path, next.value);
                }
              });
            }
          });
        },
      );

      (field.attributes.value.needs || []).forEach((path) => {
        const dep = this.getField(path);
        dep.emitter.subscribe(EFormEvent.FIELD_VALUE_CHANGE, (_ev) => {
          this.advanceField(field);
        });
      });

      if (Array.isArray(field.attributes.value.onValueChange)) {
        for (const action of field.attributes.value.onValueChange) {
          field.emitter.subscribe(EFormEvent.FIELD_VALUE_CHANGE, (ev) => {
            const nextValue = ev.field.value.value;
            switch (action.type) {
              case EFormFieldDefinitionAction.ADD_TO_SET:
                {
                  if (!isNullish(nextValue)) {
                    const val =
                      typeof nextValue === "object"
                        ? JSON.parse(JSON.stringify(nextValue))
                        : nextValue;
                    const nextKey = getRefId(val);
                    const otherField = this.getField(action.field);
                    if (otherField.attributes.value.type === "set") {
                      const valueRef = this.getValueRef(
                        action.field,
                        unref(otherField.attributes),
                      );
                      const otherValue = valueRef.value;

                      const otherFieldValue = Array.isArray(otherValue)
                        ? otherValue
                        : isNullish(otherValue) || otherValue === ""
                          ? []
                          : [otherValue];

                      if (otherFieldValue) {
                        const keys = otherFieldValue.map((it) => getRefId(it));
                        console.log({ keys, nextKey });
                        if (!keys.includes(nextKey)) {
                          valueRef.value = [...otherFieldValue, val];
                        }
                      } else {
                        console.error(`Not an array`, { val: valueRef.value });
                      }
                    }
                  }
                }
                break;
              case EFormFieldDefinitionAction.REMOVE_FROM_SET:
                {
                  if (!isNullish(nextValue)) {
                    const nextKey = getRefId(nextValue);
                    const otherField = this.getField(action.field);
                    if (otherField.attributes.value.type === "set") {
                      const valueRef = this.getValueRef(
                        action.field,
                        unref(otherField.attributes),
                      );
                      if (!Array.isArray(valueRef.value)) {
                        valueRef.value =
                          isNullish(valueRef.value) || valueRef.value === ""
                            ? []
                            : [valueRef.value];
                      }
                      if (Array.isArray(valueRef.value)) {
                        valueRef.value = valueRef.value.filter(
                          (it) => getRefId(it) !== nextKey,
                        );
                      }
                    }
                  }
                }
                break;
              case EFormFieldDefinitionAction.UNSET:
                {
                  setTimeout(() => {
                    this.setFieldValue(action.field, null, true);
                  }, 60);
                }
                break;
            }
          });
        }
      }
    });
  }

  private createField(
    path: string,
    fieldSchema:
      | FormFieldDefinition<State, any, ComputedData>
      | FormField<State, ComputedData>
      | FormFieldGroupDefinition<State, Dict, ComputedData>
      | FormFieldGroup<State, Dict, ComputedData>,
    children: FormFieldReference<State, ComputedData>[] = [],
  ): FormFieldReference<State, any, ComputedData> {
    const old = this.fields.get(path);
    if (old) {
      console.warn(`Duplicate field: ${path}`);
      // @ts-ignore
      return old;
    }

    const schemaPath =
      isFormField(fieldSchema) || isFormFieldGroup(fieldSchema)
        ? fieldSchema.schemaPath
        : path;

    const attribs = this.getAttribsRef(schemaPath);
    const fieldValue = this.getValueRef(path, attribs.value);

    const emitter: FormEventProducer<State> = new EventProducer();

    const emit = <
      K extends keyof FormEventMap<State>,
      Ev extends FormEventMap<State>[K],
    >(
      evType: K,
      ev: Omit<Ev, "field">,
    ) => {
      const field = this.fields.get(path);
      if (!field) return;
      // @ts-ignore
      emitter.emit(evType, { ...ev, field });
    };

    const field: FormFieldReference<State, any, ComputedData> = {
      _isFieldRef: true,
      __v_skip: true,
      schema: fieldSchema,
      path: path,
      name:
        isFormField(fieldSchema) || isFormFieldGroup(fieldSchema)
          ? fieldSchema.name
          : path,
      // @ts-ignore
      attributes: attribs as unknown as any,
      value: fieldValue,
      // @ts-ignore
      children: children,
      emitter: emitter,
      isGroup:
        children.length > 0 ||
        isFormFieldGroup(fieldSchema) ||
        isFormFieldGroupDefinition(fieldSchema),
      props: {
        placeholder: attribs.value.placeholder,
        tooltip: attribs.value.tooltip,
        type: castTypeNameToInput(
          attribs.value.type || attribs.value.transformType,
        ),
        options: attribs.value.options,
        locked: attribs.value.locked || attribs.value.disabled,
        disabled: attribs.value.locked || attribs.value.disabled,
        onFocus: () => {
          attribs.value.focused = true;
          emit(EFormEvent.FIELD_FOCUS, {});
        },
        onBlur: () => {
          attribs.value.focused = false;
          emit(EFormEvent.FIELD_BLUR, {});
        },
        dataRef: {
          attributes: attribs,
          value: fieldValue,
          setValue: (val: any, forceChange?: boolean) => {
            if (forceChange) {
              emit(EFormEvent.FIELD_VALUE_CHANGE, {});
              this.addForcedChange();
            }

            if (val === fieldValue.value) return;
            fieldValue.value = val;
          },
          setFocused: (focus: boolean) => {
            if (attribs.value.focused && focus === false) {
              emit(EFormEvent.FIELD_BLUR, {});
            } else {
              emit(EFormEvent.FIELD_FOCUS, {});
            }
            attribs.value.focused = focus;
          },
          setTouched: (touch: boolean) => {
            if (attribs.value.touched && touch === false) {
              emit(EFormEvent.FIELD_UNTOUCHED, {});
            } else {
              emit(EFormEvent.FIELD_TOUCHED, {});
            }
            attribs.value.touched = touch;
          },
        },
        reportChange: (_: any, value: any) => {
          fieldValue.value = value;
          //emit(EFormEvent.FIELD_VALUE_CHANGE, {})
        },
        fieldRef: {
          value: fieldValue,
        },
        ...(attribs.value.injectedProps || {}),
      },
    };

    field.emitter.subscribe(EFormEvent.FIELD_BLUR, (ev) => {
      ev.field.attributes.value.errors = getFormFieldErrors(
        ev.field,
        ev.field.value.value,
        false,
      );
    });

    field.emitter.subscribe(EFormEvent.FIELD_FOCUS, (ev) => {
      ev.field.attributes.value.errors = (
        ev.field.attributes.value.errors || []
      ).map((it) => ({
        ...it,
        disabled: true,
      }));
    });

    this.fields.set(field.path, field);

    return field;
  }

  updateLayout() {
    const next = getNextLayoutNode(
      this.layoutDefinition.value,
      this.state.value,
      this.layoutCache,
      this,
    );
    patchLayout(this.layout.value, next);
  }

  update(): FormUpdate<State, ComputedData, ExternalData> {
    const next = (this.lastUpdate = getNextFormState(
      {
        schema: this.props.schema,
        layout: this.layoutDefinition.value,
        cache: this.layoutCache,
        previousState: this.state.value,
        computedData: this.computedData.value,
      },
      this,
    ));
    diffAndPatch(
      {
        oldObject: this.schema.value,
        newObject: next.schema,
      },
      {
        keyFilter: (key) => {
          return key !== "injectedProps";
        },
      },
    );
    diffAndPatch({
      oldObject: this.state.value,
      newObject: next.state,
    });
    this.updateErrors();
    this.reloadComputations();
    patchLayout(this.layout.value, next.layout);

    return {
      schema: this.schema.value,
      state: this.state.value,
      layout: this.layout.value,
    };
  }

  handleFieldValueChanged(path: string, nextValue: any) {
    insert(this.state.value, path, nextValue);
    const field = this.getField(path);
    field.emitter.emit(EFormEvent.FIELD_VALUE_CHANGE, {
      field: field as unknown as any,
    });

    this.updateErrors();
    this.updateLayout();

    this.touched.value = true;

    this.nextComputedData();

    //    this.update();

    //console.log(JSON.stringify(this.state.value, undefined, 2));
    //this.update()
  }

  getValueRef(
    path: string,
    attribs: FormFieldReferenceAttributes<State, ComputedData>,
  ) {
    return customRef((track, trigger) => {
      return {
        get: () => {
          const val = pick(this.state.value, path);
          track();
          return attribs.transformInverse
            ? attribs.transformInverse(val)
            : castToInput(val, attribs);
        },
        set: (nextValue) => {
          const casted = attribs.transform
            ? attribs.transform(nextValue)
            : castToState(nextValue, attribs);
          if (pick(this.state.value, path) !== casted) {
            this.handleFieldValueChanged(path, casted);
          }
          trigger();
        },
      };
    });
  }

  getAttribsRef(path: string) {
    const old = this.attributeRefs.get(path);
    if (old) return old;
    const attr = customRef<FormFieldReferenceAttributes<State, ComputedData>>(
      (track, trigger) => {
        return {
          get: () => {
            const val = pick(this.schema.value, path) || {};

            if (isFormFieldGroup(val) || isFormFieldGroupDefinition(val)) {
              track();
              return val.attributes || {};
            }
            if (isFormField(val)) {
              if (val.schemaPath) {
                const p = pick(this.schema.value, val.schemaPath);
                if (p) {
                  track();
                  return p as unknown as any;
                }
              }
            }
            track();
            return val;
          },
          set: (nextValue) => {
            insert(this.schema.value, path, nextValue);
            trigger();
          },
        };
      },
    );
    this.attributeRefs.set(path, attr);
    return attr;
  }

  getParentGroup(
    path: string,
  ): FormFieldReference<State, any, ComputedData> | null {
    const parentPath = path.split(".").slice(0, -1).join(".");
    const k = pick(this.props.schema, parentPath);
    if (isFormFieldGroupDefinition(k) || isFormFieldGroup(k))
      return this.getField(parentPath);
    return null;
  }

  getField(path: string): FormFieldReference<State, any, ComputedData> {
    const field = this.fields.get(path);
    if (!field) {
      console.error(
        new Error(`Could not find field ${path}, returning a temporary one.`),
      );
      return this.createField(path, defineField({}));
    }
    return field;
  }

  onFieldEvent<K extends keyof FormEventMap<State>, Path extends string>(
    path: Path,
    evType: K,
    fun: (
      ev: Omit<FormEventMap<State>[K], "field"> & {
        field: FormFieldReference<
          State,
          Get<State, Path> | null | undefined,
          ComputedData
        >;
      },
    ) => void,
  ) {
    const field = this.getField(path);
    return field.emitter.subscribe<K>(evType, (ev) => {
      fun({
        ...ev,
        field: field as FormFieldReference<
          State,
          Get<State, Path> | null | undefined,
          ComputedData
        >,
      });
    });
  }

  setFieldValue(path: string, value: any, silent?: boolean) {
    if (silent) {
      insert(this.state.value, path, value);
      return;
    }

    const field = this.getField(path);
    this.handleFieldValueChanged(
      path,
      castToState(value, field.attributes.value),
    );
    //  const field = this.getField(path);

    //  field.value.value = value
    //insert(state.value, path, value);
    //updateErrors();
  }

  updateFieldValue<Path extends string>(
    path: Path,
    fun: (
      value: Get<State, Path> | null | undefined,
    ) => Get<State, Path> | null | undefined,
    silent?: boolean,
  ) {
    const field = this.getField(path);
    const nextValue = fun(field.value.value);
    this.setFieldValue(path, nextValue, silent);
  }

  setFieldAttributes(
    path: string,
    fun: (
      old: FormFieldReferenceAttributes<State, ComputedData>,
    ) => FormFieldReferenceAttributes<State, ComputedData>,
  ) {
    if (!this.fields.has(path)) {
      console.warn(`field ${path} not found.`);
    }
    const field = this.getField(path);
    field.attributes.value = fun(field.attributes.value);
  }

  setFieldLocked(path: string, lock: boolean) {
    this.setFieldAttributes(path, (attr) => ({
      ...attr,
      locked: lock,
      disabled: lock,
    }));
  }

  forceSetState(nextState: State) {
    this.state.value = {...nextState};
  }

  setState(nextState: State) {
    this.state.value = nextState;
    this.update();
  }

  resetState(options: {
    resetToOriginal?: boolean;
  } = {}) {
    if (options.resetToOriginal && this.originalState) {
      this.setState(this.originalState);
    } else {
      this.setState(schemaToState(this.props.schema));
    }
    this.touched.value = false;
    this.resetForcedChanges();
  }

  watchState(
    nextState: Ref<State | null | undefined>,
    callback?: (state: State) => any,
    immediate?: boolean,
  ): () => void {
    return watch(
      nextState,
      (next) => {
        if (!next) return;
        this.setState(next);
        this.watchingState.value = clone(this.state.value);
        if (callback) {
          callback(next);
        }
        if (!this.originalState) {
          this.originalState = clone(next);
        }
      },
      { immediate },
    );
  }

  cast(): State {
    const copy = JSON.parse(JSON.stringify(this.state.value));
    this.fields.forEach((field) => {
      if (!field.isGroup) {
        const attribs = field.attributes.value;
        if (attribs.transform) {
          const val = pick(copy, field.path);
          insert(copy, field.path, attribs.transform(val));
        } else {
          const value = castToState(field.value.value, field.attributes.value);
          insert(copy, field.path, value);
        }
      }
    });
    return copy as State;
  }

  upgrade() {
    this.version.value = this.version.value + 1;
  }

  setLayout(nextLayout: FormLayoutDefinition<State, ComputedData, ExternalData>) {
    this.layoutCache.clear();
    this.layoutDefinition.value = nextLayout;
    this.update();
    this.linkLayoutsWithFields(this.layout.value);
  }

  updateAllFieldErrors() {
    traverse(this.state.value, (_key, value, path) => {
      const field = this.fields.get(path);
      if (field) {
        if (!field.attributes.value.errors) {
          field.attributes.value.errors = getFormFieldErrors(field, value);
        } else {
          const nextErrors = getFormFieldErrors(field, value);
          patchArray(field.attributes.value.errors, nextErrors);
        }
      }
    });
  }

  updateErrorList() {
    this.errors.value = Array.from(this.fields.values())
      .map((field) =>
        (field.attributes.value.errors || []).map((err) => ({
          ...err,
          path: field.path,
        })),
      )
      .flat()
      .filter((it) => !!it.message && it.message.length > 1)
      .filter((it) => !!getLayoutByFieldPath(it.path, this.layout.value));
  }

  updateErrors() {
    this.updateAllFieldErrors();
    this.updateErrorList();
  }

  clearErrors() {
    this.errors.value = [];
  }

  addForcedChange() {
    this.forcedChanges.value = this.forcedChanges.value + 1;
  }

  resetForcedChanges() {
    this.forcedChanges.value = 0;
  }

  markAsSaved() {
    this.watchingState.value = clone(this.state.value);
    this.forcedChanges.value = 0;
  }
}
